import { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * useFooterLinks — Footer columns (Collections / Support / Company + the
 * unmanaged Shop-column items) sourced from /api/footer-links.
 *
 * Module-level cache so the same page-load doesn't refetch every navigation.
 * A hard reload busts the cache, which is the right contract because admin
 * edits go live only after the next visitor reloads.
 *
 * Returns:
 *   columns: { shop: [...], collections: [...], support: [...], company: [...] }
 *   loaded:  true once the API resolved (or failed) — Footer uses this to
 *            decide between "render from cache/API" vs "graceful empty cols"
 */
let _cache = null;
let _inflight = null;

const EMPTY = { shop: [], collections: [], support: [], company: [] };

function fetchOnce() {
  if (_cache) return Promise.resolve(_cache);
  if (_inflight) return _inflight;
  _inflight = api.get("/footer-links")
    .then((r) => {
      const rows = Array.isArray(r.data) ? r.data : [];
      const grouped = { ...EMPTY };
      for (const it of rows) {
        const col = it.column;
        if (grouped[col]) grouped[col].push(it);
      }
      _cache = grouped;
      return _cache;
    })
    .catch(() => EMPTY)
    .finally(() => { _inflight = null; });
  return _inflight;
}

export function useFooterLinks() {
  const [columns, setColumns] = useState(_cache || EMPTY);
  const [loaded, setLoaded] = useState(_cache !== null);

  useEffect(() => {
    if (_cache) {
      setLoaded(true);
      return;
    }
    fetchOnce().then((g) => {
      setColumns(g);
      setLoaded(true);
    });
  }, []);

  return { columns, loaded };
}
